import { formatBackupCodeForRequest } from '@/lib/auth/backup-code'
import { getOrCreateDeviceId } from '@/lib/utils/device-id'
import { orderXClient } from '@/service/client'
import {
  Confirm2faSetupRequest,
  Confirm2faSetupResponse,
  ConfirmPasskeyLoginRequest,
  ConfirmPasskeyLoginResponse,
  ConfirmPasskeySetupRequest,
  ConfirmPasskeySetupResponse,
  DeletePasskeyRequest,
  DeletePasskeyResponse,
  Disable2faRequest,
  Disable2faResponse,
  Get2faBackupCodesRequest,
  Get2faBackupCodesResponse,
  GetUserPasskeysRequest,
  GetUserPasskeysResponse,
  PasskeyLoginRequest,
  PasskeyLoginResponse,
  Setup2faRequest,
  Setup2faResponse,
  SetupPasskeyRequest,
  SetupPasskeyResponse,
  Verify2faLoginRequest,
  Verify2faLoginResponse,
  Verify2faRequest,
  Verify2faResponse,
} from '@/types/generated/common/auth'
import { ServerMessage } from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

// 2FA management
export async function setup2FA(payload: Setup2faRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { setup2fa: payload })
    const setup2faResponse = data.setup2faResponse as Setup2faResponse
    if (!setup2faResponse?.success) {
      return Promise.reject({
        status: 400,
        message: setup2faResponse?.errorMessage || 'Failed to setup 2FA',
      })
    }
    return setup2faResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function confirm2FASetup(payload: Confirm2faSetupRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { confirm2faSetup: payload })
    const confirm2faSetupResponse = data.confirm2faSetupResponse as Confirm2faSetupResponse
    if (!confirm2faSetupResponse?.success) {
      return Promise.reject({
        status: 400,
        message: confirm2faSetupResponse?.message || 'Failed to confirm 2FA setup',
      })
    }
    return confirm2faSetupResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function verify2FA(payload: Verify2faRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { verify2fa: payload })
    const verify2faResponse = data.verify2faResponse as Verify2faResponse
    if (!verify2faResponse?.success) {
      return Promise.reject({
        status: 400,
        message: verify2faResponse?.errorMessage || 'Failed to verify 2FA',
      })
    }
    return verify2faResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function verify2FALogin(payload: Verify2faLoginRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      verify2faLogin: { ...payload, deviceId: getOrCreateDeviceId() },
    })
    const verify2faLoginResponse = data.verify2faLoginResponse as Verify2faLoginResponse
    if (!verify2faLoginResponse?.success) {
      return Promise.reject({
        status: 400,
        message: verify2faLoginResponse?.errorMessage || 'Failed to verify 2FA login',
      })
    }
    return verify2faLoginResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function disable2FA(payload: Disable2faRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      disable2fa: {
        ...payload,
        ...(payload.backupCode !== undefined && {
          backupCode: formatBackupCodeForRequest(payload.backupCode),
        }),
      },
    })
    const disable2faResponse = data.disable2faResponse as Disable2faResponse
    if (!disable2faResponse?.success) {
      return Promise.reject({
        status: 400,
        message: disable2faResponse?.errorMessage || 'Failed to disable 2FA',
      })
    }
    return disable2faResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function get2FABackupCodes(payload: Get2faBackupCodesRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { get2faBackupCodes: payload })
    const get2faBackupCodesResponse = data.get2faBackupCodesResponse as Get2faBackupCodesResponse
    if (!get2faBackupCodesResponse?.success) {
      return Promise.reject({
        status: 400,
        message: get2faBackupCodesResponse?.errorMessage || 'Failed to get 2FA backup codes',
      })
    }
    return get2faBackupCodesResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

// Passkey management
export async function setupPasskey(payload: SetupPasskeyRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { setupPasskey: payload })
    const setupPasskeyResponse = data.setupPasskeyResponse as SetupPasskeyResponse
    if (!setupPasskeyResponse?.success) {
      return Promise.reject({
        status: 400,
        message: setupPasskeyResponse?.errorMessage || 'Failed to setup passkey',
      })
    }
    return setupPasskeyResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function confirmPasskeySetup(payload: ConfirmPasskeySetupRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      confirmPasskeySetup: payload,
    })
    const confirmPasskeySetupResponse =
      data.confirmPasskeySetupResponse as ConfirmPasskeySetupResponse
    if (!confirmPasskeySetupResponse?.success) {
      return Promise.reject({
        status: 400,
        message: confirmPasskeySetupResponse?.errorMessage || 'Failed to confirm passkey setup',
      })
    }
    return confirmPasskeySetupResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function passkeyLogin(payload: PasskeyLoginRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { passkeyLogin: payload })
    const passkeyLoginResponse = data.passkeyLoginResponse as PasskeyLoginResponse
    if (!passkeyLoginResponse?.success) {
      return Promise.reject({
        status: 400,
        message: passkeyLoginResponse?.errorMessage || 'Failed to login with passkey',
      })
    }
    return passkeyLoginResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function confirmPasskeyLogin(payload: ConfirmPasskeyLoginRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      confirmPasskeyLogin: { ...payload, deviceId: getOrCreateDeviceId() },
    })
    const confirmPasskeyLoginResponse =
      data.confirmPasskeyLoginResponse as ConfirmPasskeyLoginResponse
    if (!confirmPasskeyLoginResponse?.success) {
      return Promise.reject({
        status: 400,
        message: confirmPasskeyLoginResponse?.errorMessage || 'Failed to confirm passkey login',
      })
    }
    return confirmPasskeyLoginResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function getUserPasskeys(payload: GetUserPasskeysRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { getUserPasskeys: payload })
    const getUserPasskeysResponse = data.getUserPasskeysResponse as GetUserPasskeysResponse
    return getUserPasskeysResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function deletePasskey(payload: DeletePasskeyRequest) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { deletePasskey: payload })
    const deletePasskeyResponse = data.deletePasskeyResponse as DeletePasskeyResponse
    if (!deletePasskeyResponse?.success) {
      return Promise.reject({
        status: 400,
        message: deletePasskeyResponse?.errorMessage || 'Failed to delete passkey',
      })
    }
    return deletePasskeyResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function fetchUserPasskeyList() {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', { getUserPasskeys: {} })
    const fetchUserPasskeyListResponse = data.getUserPasskeysResponse as GetUserPasskeysResponse
    if (!fetchUserPasskeyListResponse?.success) {
      return Promise.reject({
        status: 400,
        message: fetchUserPasskeyListResponse?.errorMessage || 'Failed to fetch user passkey list',
      })
    }
    return fetchUserPasskeyListResponse
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}
